import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Snackbar,
  Divider,
  Drawer,
  Chip,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Description as QuoteIcon,
  Send as SendIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  SwapHoriz as ConvertIcon,
  Refresh,
  HistoryEdu as ReviseIcon,
  History as HistoryIcon,
  CompareArrows as CompareIcon,
  ArrowUpward,
  ArrowDownward,
  Drafts,
  Send as SendStat,
  PriceCheck,
  TrendingUp,
} from '@mui/icons-material';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { RouterLink } from '../../utils/linkNav';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';

interface QuotationItem {
  id?: number;
  productId: number;
  productName?: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  gstRate: number;
  lineTotal?: number;
}

interface Quotation {
  id: number;
  quotationNumber: string;
  customerId: number;
  customerName: string;
  customerContact?: string;
  quotationDate: string;
  validUntil?: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  totalAmountFormatted: string;
  status: string;
  createdByName?: string;
  createdAt: string;
  items?: QuotationItem[];
}

// ---- Revision / comparison shapes (mirror quotationRevisionService.js) ----
interface RevisionRow {
  id: number;
  revisionNumber: number;
  quotationNumber: string;
  subtotal: number;
  taxTotal: number;
  totalAmount: number;
  status: string;
  changeSummary?: string | null;
  createdByName?: string | null;
  createdAt: string;
  lineCount?: number | null;
}

interface LineFingerprint {
  product: string;
  productId?: number | null;
  quantity: number;
  rate: number;
  discount: number;
  tax: number;
  lineTotal: number;
  margin?: number | null;
}

interface Comparison {
  from: { revisionNumber: number; source: string; quotationNumber?: string; status?: string };
  to: { revisionNumber: number; source: string; quotationNumber?: string; status?: string };
  header: Array<{ field: string; label: string; previous: any; current: any }>;
  lines: Array<{ product: string; change: 'ADDED' | 'REMOVED' | 'CHANGED'; previous: LineFingerprint | null; current: LineFingerprint | null }>;
  totals: {
    previous: { subtotal: number; taxTotal: number; totalAmount: number };
    current: { subtotal: number; taxTotal: number; totalAmount: number };
    difference: number;
  };
}

const STATUSES = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CONVERTED', label: 'Converted' },
];

const STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  SENT: 'info',
  ACCEPTED: 'success',
  REJECTED: 'error',
  EXPIRED: 'warning',
  CONVERTED: 'success',
};

const getStatusLabel = (status: string) => STATUSES.find((s) => s.value === status)?.label || status;
const getStatusTone = (status: string): StatusTone => STATUS_TONE[status] || 'neutral';

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const QuotationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteConfirm, setDeleteConfirm] = useState<Quotation | null>(null);
  const [statusDialog, setStatusDialog] = useState<{ open: boolean; quotation: Quotation | null; action: string }>({
    open: false,
    quotation: null,
    action: '',
  });
  const [convertDialog, setConvertDialog] = useState<Quotation | null>(null);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);

  // Revision history + comparison state
  const [revisionsFor, setRevisionsFor] = useState<Quotation | null>(null);
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [compareFrom, setCompareFrom] = useState<string>('');
  const [compareTo, setCompareTo] = useState<string>('current');
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [comparing, setComparing] = useState(false);

  const fetchQuotations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);

      const data = await api.get(`/sales/quotations?${params.toString()}`);
      setQuotations(data.quotations || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quotations');
      console.error('Error fetching quotations:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  const handleDeleteQuotation = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/sales/quotations/${deleteConfirm.id}`);
      setSnackbar({ open: true, message: 'Quotation deleted successfully', severity: 'success' });
      setDeleteConfirm(null);
      fetchQuotations();
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to delete quotation', severity: 'error' });
    }
  };

  const handleStatusChange = async () => {
    if (!statusDialog.quotation) return;
    try {
      await api.put(`/sales/quotations/${statusDialog.quotation.id}/status`, { status: statusDialog.action });
      setSnackbar({ open: true, message: `Quotation ${statusDialog.action.toLowerCase()} successfully`, severity: 'success' });
      setStatusDialog({ open: false, quotation: null, action: '' });
      fetchQuotations();
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update status', severity: 'error' });
    }
  };

  const handleConvertToSalesOrder = async () => {
    if (!convertDialog) return;
    try {
      const result = await api.post<{ salesOrder: { id: number } }>(
        `/sales/quotations/${convertDialog.id}/convert`,
        { orderDate }
      );
      setConvertDialog(null);
      fetchQuotations();
      const newOrderId = result?.salesOrder?.id;
      if (newOrderId) {
        navigate(`/sales/orders/${newOrderId}`);
      } else {
        setSnackbar({ open: true, message: 'Quotation converted to Sales Order successfully', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to convert quotation', severity: 'error' });
    }
  };

  // Revise: snapshot the current version, then open the full-page edit form on
  // the working copy (which stays DRAFT, so it remains editable).
  const handleRevise = async (quotation: Quotation) => {
    try {
      const res = await api.post(`/sales/quotations/${quotation.id}/revise`, {});
      const newRev = res.revisionNumber ?? 1;
      setSnackbar({
        open: true,
        message: `Prior version saved as Rev ${newRev - 1}. Editing revision ${newRev}.`,
        severity: 'success',
      });
      navigate(`/sales/quotations/${quotation.id}/edit`);
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to revise quotation', severity: 'error' });
    }
  };

  const openRevisions = async (quotation: Quotation) => {
    setRevisionsFor(quotation);
    setRevisions([]);
    setComparison(null);
    setCompareTo('current');
    setCompareFrom('');
    setRevisionsLoading(true);
    try {
      const data = await api.get(`/sales/quotations/${quotation.id}/revisions`);
      const rows: RevisionRow[] = data.revisions || [];
      setRevisions(rows);
      if (rows.length > 0) setCompareFrom(String(rows[0].revisionNumber));
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to load revisions', severity: 'error' });
    } finally {
      setRevisionsLoading(false);
    }
  };

  const runComparison = async () => {
    if (!revisionsFor) return;
    if (compareFrom === '' && compareTo === '') {
      setSnackbar({ open: true, message: 'Select at least one revision to compare', severity: 'error' });
      return;
    }
    setComparing(true);
    setComparison(null);
    try {
      const params = new URLSearchParams();
      if (compareFrom !== '') params.append('from', compareFrom);
      if (compareTo !== '') params.append('to', compareTo);
      const data = await api.get(`/sales/quotations/${revisionsFor.id}/compare?${params.toString()}`);
      setComparison(data.comparison || null);
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to compare revisions', severity: 'error' });
    } finally {
      setComparing(false);
    }
  };

  const filteredQuotations = quotations.filter((q) => {
    const matchesSearch =
      q.quotationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || q.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const hasFilters = !!(searchTerm || filterStatus);
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
  };

  // KPI cards derived from the loaded list -- null while loading so a slow
  // fetch never briefly reads as "zero".
  const totalCount = loading ? null : quotations.length;
  const draftCount = loading ? null : quotations.filter((q) => q.status === 'DRAFT').length;
  const sentCount = loading ? null : quotations.filter((q) => q.status === 'SENT').length;
  const acceptedValue = loading
    ? null
    : quotations.filter((q) => q.status === 'ACCEPTED').reduce((sum, q) => sum + Number(q.totalAmount || 0), 0);
  const convertedCount = quotations.filter((q) => q.status === 'CONVERTED').length;
  const conversionRate = loading || quotations.length === 0 ? null : Math.round((convertedCount / quotations.length) * 100);

  const deltaColor = (n: number) => (n > 0 ? 'success.main' : n < 0 ? 'error.main' : 'text.secondary');
  const DeltaArrow = ({ n }: { n: number }) =>
    n > 0 ? <ArrowUpward sx={{ fontSize: 14 }} /> : n < 0 ? <ArrowDownward sx={{ fontSize: 14 }} /> : null;

  return (
    <Box>
      <PageHeader
        title="Quotations"
        subtitle="Create, revise and compare customer price quotations"
        actions={
          <>
            <IconButton onClick={fetchQuotations} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/sales/quotations/new"
            >
              New Quotation
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Quotations" value={totalCount} icon={<QuoteIcon />} color="#2563eb" />
        <StatCard title="Draft" value={draftCount} icon={<Drafts />} color="#64748b" />
        <StatCard title="Sent" value={sentCount} icon={<SendStat />} color="#0891b2" />
        <StatCard
          title="Accepted Value"
          value={acceptedValue === null ? null : formatIndianNumber(acceptedValue)}
          icon={<PriceCheck />}
          color="#059669"
        />
        <StatCard
          title="Conversion"
          value={conversionRate === null ? null : `${conversionRate}%`}
          icon={<TrendingUp />}
          color="#7c3aed"
          share={conversionRate === null ? undefined : conversionRate / 100}
        />
      </Box>

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search quotations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Status</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          {filteredQuotations.length} quotations ·{' '}
          {formatCurrency(filteredQuotations.reduce((sum, q) => sum + Number(q.totalAmount || 0), 0))}
        </Typography>
      </FilterBar>

      {/* Quotations Table */}
      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Quotation #', 'Customer', 'Date', 'Valid Until', 'Amount', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchQuotations} />
                  </TableCell>
                </TableRow>
              ) : filteredQuotations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No quotations found"
                      hint={hasFilters ? 'Try adjusting your search criteria' : 'Start by creating your first quotation'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuotations.map((quotation) => (
                  <TableRow key={quotation.id} sx={bodyRowSx} hover>
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={quotation.quotationNumber} tint="#2563eb" icon={<QuoteIcon fontSize="small" />} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {quotation.customerName || `Customer ${quotation.customerId}`}
                      </Typography>
                      {quotation.customerContact && (
                        <Typography variant="caption" color="text.secondary">
                          {quotation.customerContact}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{fmtDate(quotation.quotationDate)}</TableCell>
                    <TableCell>{fmtDate(quotation.validUntil)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {quotation.totalAmountFormatted || formatCurrency(quotation.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={getStatusLabel(quotation.status)} tone={getStatusTone(quotation.status)} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Revision history">
                          <IconButton size="small" onClick={() => openRevisions(quotation)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {quotation.status === 'DRAFT' && (
                          <>
                            <Tooltip title="Revise (snapshot then edit)">
                              <IconButton size="small" color="secondary" onClick={() => handleRevise(quotation)}>
                                <ReviseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="primary"
                                component={RouterLink}
                                to={`/sales/quotations/${quotation.id}/edit`}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send to customer">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setStatusDialog({ open: true, quotation, action: 'SENT' })}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteConfirm(quotation)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {quotation.status === 'SENT' && (
                          <>
                            <Tooltip title="Accept">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setStatusDialog({ open: true, quotation, action: 'ACCEPTED' })}
                              >
                                <AcceptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setStatusDialog({ open: true, quotation, action: 'REJECTED' })}
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {quotation.status === 'ACCEPTED' && (
                          <Tooltip title="Convert to Sales Order">
                            <IconButton size="small" color="primary" onClick={() => setConvertDialog(quotation)}>
                              <ConvertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Quotation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete quotation "{deleteConfirm?.quotationNumber}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteQuotation}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, quotation: null, action: '' })}>
        <DialogTitle>
          {statusDialog.action === 'SENT' && 'Send Quotation'}
          {statusDialog.action === 'ACCEPTED' && 'Accept Quotation'}
          {statusDialog.action === 'REJECTED' && 'Reject Quotation'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {statusDialog.action === 'SENT' &&
              `Mark quotation "${statusDialog.quotation?.quotationNumber}" as sent to customer?`}
            {statusDialog.action === 'ACCEPTED' && `Accept quotation "${statusDialog.quotation?.quotationNumber}"?`}
            {statusDialog.action === 'REJECTED' && `Reject quotation "${statusDialog.quotation?.quotationNumber}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, quotation: null, action: '' })}>Cancel</Button>
          <Button
            variant="contained"
            color={statusDialog.action === 'REJECTED' ? 'error' : 'primary'}
            onClick={handleStatusChange}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Sales Order Dialog */}
      <Dialog open={!!convertDialog} onClose={() => setConvertDialog(null)}>
        <DialogTitle>Convert to Sales Order</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Convert quotation "{convertDialog?.quotationNumber}" to a Sales Order?</Typography>
          <TextField
            fullWidth
            label="Order Date"
            type="date"
            value={orderDate}
            onChange={(e) => setOrderDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleConvertToSalesOrder}>
            Convert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revisions + Comparison Drawer */}
      <Drawer
        anchor="right"
        open={!!revisionsFor}
        onClose={() => setRevisionsFor(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 }, p: 3 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Revision History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {revisionsFor?.quotationNumber}
            </Typography>
          </Box>
          <IconButton onClick={() => setRevisionsFor(null)}>
            <RejectIcon />
          </IconButton>
        </Box>

        {revisionsLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            {revisions.length === 0 ? (
              <Alert severity="info" sx={{ my: 2 }}>
                No prior revisions yet. Use the Revise action on a draft to snapshot the current version before editing.
              </Alert>
            ) : (
              <Stack spacing={1.25} sx={{ my: 2 }}>
                {revisions.map((r) => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" color="primary" label={`Rev ${r.revisionNumber}`} />
                        <Typography variant="body2" fontWeight={700}>
                          {r.quotationNumber}
                        </Typography>
                        <StatusDot label={getStatusLabel(r.status)} tone={getStatusTone(r.status)} />
                      </Box>
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(r.totalAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(r.createdAt)}
                        {r.createdByName ? ` · ${r.createdByName}` : ''}
                        {r.lineCount != null ? ` · ${r.lineCount} lines` : ''}
                      </Typography>
                    </Box>
                    {r.changeSummary && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        “{r.changeSummary}”
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}

            {/* Comparison controls */}
            <Divider sx={{ my: 2 }}>
              <Chip icon={<CompareIcon />} label="Compare" size="small" />
            </Divider>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
              <TextField
                select
                size="small"
                label="From"
                value={compareFrom}
                onChange={(e) => setCompareFrom(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                {revisions.map((r) => (
                  <MenuItem key={r.id} value={String(r.revisionNumber)}>
                    Rev {r.revisionNumber}
                  </MenuItem>
                ))}
                <MenuItem value="current">Current (live)</MenuItem>
              </TextField>
              <CompareIcon sx={{ color: 'text.disabled' }} />
              <TextField
                select
                size="small"
                label="To"
                value={compareTo}
                onChange={(e) => setCompareTo(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                {revisions.map((r) => (
                  <MenuItem key={r.id} value={String(r.revisionNumber)}>
                    Rev {r.revisionNumber}
                  </MenuItem>
                ))}
                <MenuItem value="current">Current (live)</MenuItem>
              </TextField>
              <Button variant="contained" size="small" onClick={runComparison} disabled={comparing}>
                {comparing ? 'Comparing…' : 'Compare'}
              </Button>
            </Box>

            {comparison && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Rev {comparison.from.revisionNumber} →{' '}
                  {comparison.to.source === 'live' ? 'Current' : `Rev ${comparison.to.revisionNumber}`}
                </Typography>

                {/* Totals */}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Previous Total
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(comparison.totals.previous.totalAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Change
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: deltaColor(comparison.totals.difference), display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <DeltaArrow n={comparison.totals.difference} />
                        {formatCurrency(Math.abs(comparison.totals.difference))}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">
                        Current Total
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(comparison.totals.current.totalAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Header changes */}
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                  Header Changes
                </Typography>
                {comparison.header.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No header changes.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, mt: 0.5, borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Field</TableCell>
                          <TableCell sx={headerCellSx}>Previous</TableCell>
                          <TableCell sx={headerCellSx}>Current</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparison.header.map((h) => (
                          <TableRow key={h.field} sx={bodyRowSx}>
                            <TableCell>{h.label}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>
                              {h.previous == null || h.previous === '' ? '—' : String(h.previous)}
                            </TableCell>
                            <TableCell sx={{ color: 'success.main' }}>
                              {h.current == null || h.current === '' ? '—' : String(h.current)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Line changes */}
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                  Line Changes
                </Typography>
                {comparison.lines.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No line changes.
                  </Typography>
                ) : (
                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                    {comparison.lines.map((ln, idx) => {
                      const tone = ln.change === 'ADDED' ? 'success' : ln.change === 'REMOVED' ? 'error' : 'warning';
                      return (
                        <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={700}>
                              {ln.product}
                            </Typography>
                            <Chip size="small" color={tone as any} label={ln.change} variant="outlined" />
                          </Box>
                          {ln.change === 'CHANGED' && ln.previous && ln.current && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'center' }}>
                              {(
                                [
                                  ['Qty', 'quantity', false],
                                  ['Rate', 'rate', true],
                                  ['Disc %', 'discount', false],
                                  ['Tax', 'tax', true],
                                  ['Line Total', 'lineTotal', true],
                                ] as Array<[string, keyof LineFingerprint, boolean]>
                              ).map(([label, key, isCurrency]) => {
                                const pv = Number(ln.previous![key] ?? 0);
                                const cv = Number(ln.current![key] ?? 0);
                                if (pv === cv) return null;
                                const diff = cv - pv;
                                return (
                                  <React.Fragment key={label}>
                                    <Typography variant="caption" sx={{ color: 'error.main', textAlign: 'left' }}>
                                      {label}: {isCurrency ? formatCurrency(pv) : pv}
                                    </Typography>
                                    <Box sx={{ color: deltaColor(diff), display: 'flex', alignItems: 'center' }}>
                                      <DeltaArrow n={diff} />
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'success.main', textAlign: 'right' }}>
                                      {label}: {isCurrency ? formatCurrency(cv) : cv}
                                    </Typography>
                                  </React.Fragment>
                                );
                              })}
                            </Box>
                          )}
                          {ln.change === 'ADDED' && ln.current && (
                            <Typography variant="caption" color="text.secondary">
                              Qty {ln.current.quantity} · {formatCurrency(ln.current.rate)} · Line {formatCurrency(ln.current.lineTotal)}
                            </Typography>
                          )}
                          {ln.change === 'REMOVED' && ln.previous && (
                            <Typography variant="caption" color="text.secondary">
                              Was: Qty {ln.previous.quantity} · {formatCurrency(ln.previous.rate)} · Line {formatCurrency(ln.previous.lineTotal)}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}
          </>
        )}
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuotationsPage;
